import React, { useEffect, useState } from 'react';
import Container from '../../../components/Container';
import Header from '../../../components/Header';
import { translate } from '../../../services/translate';
import { useNavigation, NavigationProp, useIsFocused, RouteProp, useRoute } from '@react-navigation/native';
import CustomButton from '../../../components/CustomButton';
import { GuardianAppParamList } from '../../../typings/Params';
import Card from '../../../components/Card';
import { Tabs } from '../../../components/Tabs';
import { useForm } from 'react-hook-form';
import { useQuery } from '@tanstack/react-query';
import { guardianAppService } from '../../../services/central-api/guardian-app';
import { ActivityIndicator, ScrollView, View } from 'react-native';
import FormInput from '../../../components/FormInput';
import MyCpfInput from '../../../components/MyCpfInput';
import MyDateInput from '../../../components/MyDateInput';
import MyEmailInput from '../../../components/MyEmailInput';
import MyPhoneInput from '../../../components/MyPhoneInput';
import { useStyles } from 'react-native-unistyles';
import Toast from 'react-native-toast-message';
import { ClientError, InvalidValue } from '../../../services/central-api/base-service';
import ProtectiveMeasuresTab from './ProtectiveMeasuresTab';
import Badge from '../../../components/Badge';
import { createFileFromUrl } from '../../third-party-approved-companies/InscriptionsCommons';

function getFileNameWithMimeType(fileName: string, mimetype?: string | null): string {
    const extension = mimetype?.split('/').pop()?.split('+')[0];
    return extension ? `${fileName}.${extension}` : fileName;
}

interface SupportedPersonForm {
    name: string | null;
    document: string | null;
    phone: string | null;
    imei: string | null;
    email: string | null;
    birth: string | null;
    address: string | null;
    complement: string | null;
}

export default function GuardianAppUserForm() {
    const route = useRoute<RouteProp<GuardianAppParamList, 'GuardianAppUser'>>();
    const assistedPersonId = route.params?.id;
    const navigation = useNavigation<NavigationProp<GuardianAppParamList>>();
    const { theme } = useStyles();
    const [protectiveMeasures, setProtectiveMeasures] = useState<ProtectiveMeasureDraft[]>([]);
    const [createdGuardianWomanId, setCreatedGuardianWomanId] = useState<number>();

    const defaultEmptyValues: SupportedPersonForm = {
        name: '',
        document: '',
        phone: '',
        imei: null,
        email: null,
        birth: null,
        address: null,
        complement: null,
    };

    const form = useForm<SupportedPersonForm>({
        defaultValues: defaultEmptyValues,
        mode: 'onBlur',
    });

    const { data, isLoading, refetch } = useQuery({
        queryKey: ['getGuardianWomanById', assistedPersonId],
        queryFn: async () => {
            const guardianWoman = await guardianAppService.getGuardianWomanById(assistedPersonId!);
            const protectiveMeasures = await Promise.all(guardianWoman.protectiveMeasures.map(async measure => {
                const aggressorImages = await Promise.all(measure.aggressorImages.map(async photo => {
                    const fileName = getFileNameWithMimeType(
                        translate('aggressorImageFileName', { id: photo.id }),
                        photo.metadata?.mimetype,
                    );
                    const photoUrl = photo.photoUrl ?? photo.fileUrl;
                    return {
                        ...photo,
                        fileName,
                        file: photoUrl ? await createFileFromUrl(photoUrl, fileName) : undefined,
                    };
                }));
                const policeReportFileName = getFileNameWithMimeType(
                    translate('protectiveMeasureFileName', { id: measure.id }),
                    measure.policeReportMetadata?.mimetype,
                );
                const policeReportFile = measure.policeReportUrl
                    ? await createFileFromUrl(measure.policeReportUrl, policeReportFileName)
                    : undefined;

                return { ...measure, aggressorImages, policeReportFile };
            }));

            return { ...guardianWoman, protectiveMeasures };
        },
        enabled: Boolean(assistedPersonId),
    });

    const isFocused = useIsFocused();
    useEffect(() => {
        if (isFocused && assistedPersonId) {
            refetch();
        }
        if (isFocused && !assistedPersonId && !createdGuardianWomanId) {
            form.reset(defaultEmptyValues);
            setProtectiveMeasures([]);
        }
    }, [isFocused, assistedPersonId, createdGuardianWomanId]);

    useEffect(() => {
        if (!data) {
            return;
        }
        form.reset({
            ...data,
        });

        setProtectiveMeasures(data.protectiveMeasures.map(measure => ({
            ...measure,
            aggressorPhotoFiles: [],
            aggressorPhotoIdsToDelete: [],
            removePoliceReport: false,
        })));
    }, [assistedPersonId, data]);

    function goBack() {
        if (navigation.canGoBack()) {
            navigation.goBack();
        } else {
            navigation.navigate('GuardianAppUsers', { limit: 25, page: 1 });
        }
    }

    function updateDraftMeasure(previousId: number, updated: ProtectiveMeasureDraft) {
        setProtectiveMeasures(current => current.map(measure => measure.id === previousId ? updated : measure));
    }

    async function persistPendingMeasures(guardianWomanId: number) {
        for (const initialMeasure of protectiveMeasures) {
            let measure = initialMeasure;
            const isNewMeasure = !measure.id || measure.id < 0;

            if (isNewMeasure) {
                const createdMeasure = await guardianAppService.createProtectiveMeasure(guardianWomanId, measure);
                const previousId = measure.id ?? 0;
                measure = {
                    ...measure,
                    ...createdMeasure,
                    id: createdMeasure.id,
                    guardianWomanId,
                    aggressorImages: createdMeasure.aggressorImages ?? measure.aggressorImages,
                    aggressorPhotoFiles: measure.aggressorPhotoFiles,
                    aggressorPhotoIdsToDelete: [],
                    removePoliceReport: false,
                };
                updateDraftMeasure(previousId, measure);
            }

            while (measure.aggressorPhotoFiles.length > 0) {
                const photoFile = measure.aggressorPhotoFiles[0];
                const createdPhoto = await guardianAppService.addProtectiveMeasurePhoto(guardianWomanId, measure.id!, photoFile);
                measure = {
                    ...measure,
                    aggressorImages: [...measure.aggressorImages, { ...createdPhoto, file: photoFile, fileName: photoFile.name }],
                    aggressorPhotoFiles: measure.aggressorPhotoFiles.slice(1),
                };
                updateDraftMeasure(measure.id!, measure);
            }
        }
    }

    async function formSubmit() {
        try {
            const values = form.getValues();

            if (assistedPersonId) {
                await guardianAppService.editGuardianWoman({
                    id: assistedPersonId,
                    ...values,
                    imei: values.imei || undefined,
                    birth: values.birth || undefined,
                    address: values.address || undefined,
                    phone: values.phone || undefined,
                    complement: values.complement || undefined,
                    email: values.email || undefined,
                });
                Toast.show({
                    type: 'sentinelxSuccess',
                    text1: translate('ActionSuccessfully'),
                });
                goBack();
                return;
            }

            let guardianWomanId = createdGuardianWomanId;
            if (!guardianWomanId) {
                const createdPerson = await guardianAppService.createGuardianWoman({
                    ...values,
                    imei: values.imei || undefined,
                    birth: values.birth || undefined,
                    address: values.address || undefined,
                    phone: values.phone || undefined,
                    complement: values.complement || undefined,
                    email: values.email || undefined,
                });
                guardianWomanId = createdPerson.id;
                setCreatedGuardianWomanId(guardianWomanId);
            }

            await persistPendingMeasures(guardianWomanId);
            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });
            goBack();
        } catch (err) {
            if (err instanceof InvalidValue) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate('invalidValue', { field: err.field }),
                });
            }
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxWarning',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    return <Container>
        <Header
            title={translate('supportedPersonRegister')}
            goBackAction={goBack}
            actions={<CustomButton
                text={translate('save')}
                onPress={formSubmit}
                disabled={!form.formState.isValid}
            />}
        />
        <Card>
            {(Boolean(assistedPersonId) && isLoading) ? <ActivityIndicator size='large' color={theme.colors.loadingColor} /> : <Tabs
                tabs={[{
                    key: 'general',
                    label: translate('supportedPersonInformation'),
                }, {
                    key: 'protective-measures',
                    label: translate('protectiveMeasures'),
                }]}
                endContent={
                    <View style={[{ justifyContent: 'center', alignSelf: 'flex-end' }]}>
                        {data &&
                            <Badge
                                mode='SQUARE'
                                color={data.status === 'active' ? 'green' : 'orange'}
                            >{translate(data.status)}</Badge>
                        }
                    </View>
                }
            >
                {({ selectedTab }) => {
                    if (selectedTab === 'general') {
                        return <ScrollView contentContainerStyle={{ padding: 10, gap: 10 }}>
                            <FormInput
                                label={translate('fullName')}
                                fieldName='name'
                                form={form}
                                required={true}
                            />
                            <MyPhoneInput
                                label={translate('phone')}
                                form={form}
                                fieldName={'phone'}
                            />
                            <FormInput
                                label={`${translate('phone')} IMEI`}
                                fieldName='imei'
                                form={form}
                            />
                            <MyCpfInput
                                fieldName='document'
                                form={form}
                                viewStyle={{ flex: 1 }}
                                required
                            />
                            <MyEmailInput
                                label={translate('email')}
                                form={form}
                                fieldName='email'
                            />
                            <MyDateInput
                                form={form}
                                fieldName='birth'
                                label={translate('birth')}
                                allowDateBiggerThanToday={false}
                            />
                            <FormInput
                                label={translate('address')}
                                form={form}
                                fieldName='address'
                            />
                            <FormInput
                                label={translate('complement')}
                                form={form}
                                fieldName='complement'
                            />
                        </ScrollView>;
                    }
                    if (selectedTab === 'protective-measures') {
                        return <ProtectiveMeasuresTab
                            guardianWomanId={assistedPersonId ?? createdGuardianWomanId}
                            protectiveMeasures={protectiveMeasures}
                            setProtectiveMeasures={setProtectiveMeasures}
                        />;
                    }
                }}
            </Tabs>
            }
        </Card>
    </Container>;
}
